import { useApolloClient } from "@apollo/client";
import { assertNever } from "@msys/common";
import {
  CardContainer,
  ListItem,
  LoadingSpinner as LoadingContainer,
  useScreenWidth,
} from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { useUserData } from "../../auth/useUserData.js";
import { useStateWithUrlParam } from "../../commons/hooks/useStateWithUrlParam.js";
import { SHORT_LIST_RESULTS_PER_PAGE } from "../../constants.js";
import {
  TasksListQueryVariables,
  useTasksListQuery,
  useToggleTodoItemDoneMutation,
} from "../../main-routes/tasks/TasksList.generated.js";
import { TasksMineFilterButton } from "../tasks/filters/TasksMineFilterButton.js";
import { TasksOverdueFilterButton } from "../tasks/filters/TasksOverdueFilterButton.js";
import { TasksStatusFilterButton } from "../tasks/filters/TasksStatusFilterButton.js";
import { TaskIcon } from "../tasks/TaskIcon.js";
import { CreateTodoItemButton } from "./CreateTodoItemButton.js";
import { EditTodoItemModal } from "./EditTodoItemModal.js";
import { TodoListItem } from "./TodoListItem.js";
import {
  TodosBox_LinkedCrmOrganisationFragment,
  TodosBox_LinkedCrmUserFragment,
  TodosBox_LinkedProjectFragment,
} from "./TodosBox.generated.js";

interface Props {
  limit?: number;
  fixedFilters?: NonNullable<TasksListQueryVariables["filter"]>;
  linkedCrmOrganisation?: TodosBox_LinkedCrmOrganisationFragment;
  linkedCrmUser?: TodosBox_LinkedCrmUserFragment;
  linkedProject?: TodosBox_LinkedProjectFragment;
  renderSeeMoreButton?: (
    activeFilters?: NonNullable<TasksListQueryVariables["filter"]>
  ) => React.ReactElement;
  onUpdateRefetchQueries?: string[];
}

export function TodosBox(props: Props) {
  const { isMinTablet } = useScreenWidth();
  const viewer = useUserData().currentUser!;

  const [filters, setFilters] = React.useState<
    NonNullable<TasksListQueryVariables["filter"]>
  >({
    status: ["OPEN"],
  });

  return (
    <TodosBoxComponent
      quickFilters={
        isMinTablet ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <TasksStatusFilterButton
              value={filters.status ?? undefined}
              setValue={newValue =>
                setFilters(state => {
                  if (newValue !== undefined) {
                    return { ...state, status: newValue };
                  }
                  const { status, ...rest } = state;
                  return rest;
                })
              }
            />
            <TasksMineFilterButton
              value={filters.assigneeIds?.includes(viewer.id) ?? false}
              setValue={newValue =>
                setFilters(state => {
                  if (newValue === true) {
                    return { ...state, assigneeIds: [viewer.id] };
                  } else if (newValue === false) {
                    const { assigneeIds, ...rest } = state;
                    return rest;
                  } else {
                    assertNever(newValue);
                  }
                })
              }
            />
            <TasksOverdueFilterButton
              value={
                filters.timeBefore
                  ? filters.timeBefore <= new Date().toISOString()
                  : false
              }
              setValue={newValue =>
                setFilters(state => {
                  if (newValue === false) {
                    const { timeBefore, ...rest } = state;
                    return { ...rest };
                  } else if (newValue === true) {
                    return { ...state, timeBefore: new Date().toISOString() };
                  } else {
                    assertNever(newValue);
                  }
                })
              }
            />
          </Stack>
        ) : undefined
      }
      activeFilters={filters}
      {...props}
    />
  );
}

export function MyTodosBox(props: Props) {
  const { t } = useTranslate(["Tasks", "Global"]);
  const viewer = useUserData().currentUser!;
  const { isMinTablet } = useScreenWidth();

  const [filters, setFilters] = React.useState<
    NonNullable<TasksListQueryVariables["filter"]>
  >({
    status: ["OPEN"],
  });

  return (
    <TodosBoxComponent
      title={t("My to-do list", {
        ns: "Tasks",
      })}
      fixedFilters={{ assigneeIds: [viewer.id] }}
      activeFilters={filters}
      quickFilters={
        isMinTablet ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <TasksStatusFilterButton
              value={filters.status ?? undefined}
              setValue={newValue =>
                setFilters(state => {
                  if (newValue !== undefined) {
                    return { ...state, status: newValue };
                  }
                  const { status, ...rest } = state;
                  return rest;
                })
              }
            />
            <TasksOverdueFilterButton
              value={
                filters.timeBefore ? filters.timeBefore < new Date() : false
              }
              setValue={newValue =>
                setFilters(state => {
                  if (newValue === false) {
                    const { timeBefore, ...rest } = state;
                    return { ...rest };
                  } else if (newValue === true) {
                    return { ...state, timeBefore: new Date() };
                  } else {
                    assertNever(newValue);
                  }
                })
              }
            />
          </Stack>
        ) : undefined
      }
      defaultAssigneeId={viewer.id}
      disableAssigneeInput
      {...props}
    />
  );
}

function TodosBoxComponent<T extends string>({
  title,
  limit: passedLimit = SHORT_LIST_RESULTS_PER_PAGE,
  fixedFilters,
  quickFilters,
  activeFilters,
  defaultAssigneeId,
  disableAssigneeInput,
  linkedCrmOrganisation,
  linkedCrmUser,
  linkedProject,
  renderSeeMoreButton,
  onUpdateRefetchQueries,
}: Props & {
  title?: string;
  quickFilters?: React.ReactNode;
  activeFilters?: NonNullable<TasksListQueryVariables["filter"]>;
  defaultAssigneeId?: string;
  disableAssigneeInput?: boolean;
}) {
  const { t } = useTranslate(["Tasks", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const [limit, setLimit] = React.useState(passedLimit);

  const client = useApolloClient();
  const effectiveFilter: NonNullable<TasksListQueryVariables["filter"]> = {
    ...activeFilters,
    ...fixedFilters,
    linkedCrmOrganisationId: linkedCrmOrganisation?.id,
    linkedCrmUserId: linkedCrmUser?.id,
    linkedProjectId: linkedProject?.id,
  };
  const query = useTasksListQuery({
    client,
    variables: {
      limit,
      sorting: [
        {
          column: "time",
          direction: "asc",
        },
      ],
      filter: effectiveFilter,
    },
  });

  const [selectedTaskId, setSelectedTaskId] = useStateWithUrlParam<
    string | null
  >("taskId", null);

  const [toggleTodoDone] = useToggleTodoItemDoneMutation({
    client,
    refetchQueries: [namedOperations.Query.TasksList],
  });
  const tasks =
    (query.data ?? query.previousData)?.todoItems?.edges.map(e => e.node) ?? [];
  const total = (query.data ?? query.previousData)?.todoItems?.totalCount ?? 0;

  const handleToggle = async (task: { id: string; isDone: boolean }) => {
    await toggleTodoDone({
      variables: {
        input: { id: task.id, isDone: !task.isDone },
      },
    });
    enqueueSnackbar(
      task.isDone
        ? t("To-do marked as undone", { ns: "Tasks" })
        : t("To-do marked as done", { ns: "Tasks" })
    );
  };

  return (
    <CardContainer
      isExpandable
      Icon={<TaskIcon />}
      title={title ?? t("To-do list", { ns: "Tasks" })}
      ActionButton={
        <CreateTodoItemButton
          assigneeId={defaultAssigneeId}
          disableAssigneeInput={disableAssigneeInput}
          linkedProjectId={linkedProject?.id}
          linkedCrmCompanyId={linkedCrmOrganisation?.id}
          linkedCrmPersonId={linkedCrmUser?.id}
          refetchQueries={[namedOperations.Query.TasksList]}
        />
      }
    >
      <Stack direction="column" flex={1} width="100%" spacing={0}>
        <Box width="100%" pb={1} px={1} pt={1}>
          {quickFilters}
        </Box>

        {query.loading && tasks.length === 0 ? (
          <LoadingContainer />
        ) : tasks.length === 0 ? (
          <Stack
            direction="column"
            alignItems={"center"}
            padding={1}
            spacing={1}
          >
            <Typography variant="caption" color="gray">
              {t("There are no items to display", {
                ns: "Global",
              })}
            </Typography>

            <CreateTodoItemButton
              assigneeId={defaultAssigneeId}
              disableAssigneeInput={disableAssigneeInput}
              linkedProjectId={linkedProject?.id}
              linkedCrmCompanyId={linkedCrmOrganisation?.id}
              linkedCrmPersonId={linkedCrmUser?.id}
              refetchQueries={[namedOperations.Query.TasksList]}
              Button={
                <Button color="secondary" startIcon={<AddIcon />}>
                  {t("Add to-do item", {
                    ns: "Tasks",
                  })}
                </Button>
              }
            />
          </Stack>
        ) : (
          <Stack
            direction="column"
            m={-1}
            flex={1}
            style={{ overflow: "auto" }}
          >
            <Stack
              direction="column"
              spacing={0}
              style={{ overflow: "auto" }}
              p={1}
            >
              {tasks.map(task => (
                <ListItem
                  key={task.id}
                  onClick={() => setSelectedTaskId(task.id)}
                >
                  <TodoListItem task={task} onToggle={handleToggle} />
                </ListItem>
              ))}
            </Stack>
          </Stack>
        )}

        {total > limit && (
          <Box mt={1}>
            {renderSeeMoreButton ? (
              renderSeeMoreButton(effectiveFilter)
            ) : (
              <Button
                fullWidth
                sx={{ borderRadius: 0 }}
                color="secondary"
                variant="text"
                onClick={() => setLimit(limit => limit + passedLimit)}
              >
                {t("See more", { ns: "Global" })}
              </Button>
            )}
          </Box>
        )}

        {selectedTaskId !== null && (
          <EditTodoItemModal
            key={selectedTaskId}
            id={selectedTaskId}
            refetchQueries={[
              namedOperations.Query.TasksList,
              ...(onUpdateRefetchQueries ?? []),
            ]}
            disableAssigneeInput={disableAssigneeInput}
            handleClose={() => setSelectedTaskId(null)}
          />
        )}
      </Stack>
    </CardContainer>
  );
}
